import { FC, useMemo } from "react";
import { CalendarDaysIcon, XCircleIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import { ACCOUNT_DISPOSITION_STATUSES } from "shared/lib/constants/account";
import { ActionMenu, ActionMenuActionI } from "shared/ui/action-menu";
import { getCanonicalMeetingTime } from "shared/lib/helpers";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { AccountDetailsStatusI, AccountI } from "shared/lib/interfaces/account";
import {
  AccountCancelMeetingConfirmationModal,
  CONFIRM_CANCEL_MEETING_MODAL_ID,
} from "@/modals/account-cancel-meeting-confirmation-modal";
import { CampaignI } from "@/lib/types/campaign";
import { ActionMoveToNurture } from "@/components/modules/pipeline/account-details/sections/status-section/move-to-nurture";
import { useModifyMeeting } from "@/modules/pipeline/account-details/hooks/use-modify-meeting";
import { AccountTopTenAction } from "@/modules/top-ten/components/account-top-ten-action";

dayjs.extend(isSameOrAfter);

interface StatusSectionActionsPropsI {
  campaign?: CampaignI;
  account?: AccountI;
  accountStatus?: AccountDetailsStatusI;
  onAccountUpdate?: () => void;
}

export const StatusSectionActions: FC<StatusSectionActionsPropsI> = ({
  campaign,
  account,
  accountStatus,
  onAccountUpdate,
}) => {
  const isCold =
    accountStatus?.status === ACCOUNT_DISPOSITION_STATUSES.COLD_LEAD;
  const isMeetingScheduled =
    accountStatus?.status === ACCOUNT_DISPOSITION_STATUSES.MEETING_SCHEDULED;
  // GLE-8044: past-check uses canonical meeting time (BMCE.start_time → ... → meeting_time)
  // so ICS-driven reschedules flip the past/future split correctly.
  const canonicalMeetingTime = accountStatus
    ? getCanonicalMeetingTime(accountStatus)
    : null;
  const isMeetingInPast =
    isMeetingScheduled &&
    !!canonicalMeetingTime &&
    dayjs(canonicalMeetingTime).isBefore(dayjs());

  const handleModifyMeeting = useModifyMeeting();

  const meetingScheduledActionItems: ActionMenuActionI[] = useMemo(() => {
    if (isMeetingScheduled && !isMeetingInPast) {
      return [
        {
          title: "Modify meeting",
          icon: <CalendarDaysIcon className="mr-1.5 w-4" />,
          handler: handleModifyMeeting,
        },
        {
          title: "Cancel meeting",
          icon: <XCircleIcon className="mr-1.5 w-4" />,
          handler: () => {
            modalHelpers.lazyOpen(CONFIRM_CANCEL_MEETING_MODAL_ID);
          },
        },
      ];
    }

    return [];
  }, [handleModifyMeeting, isMeetingScheduled, isMeetingInPast]);

  return (
    <div className="flex items-center gap-3">
      {isMeetingScheduled && isMeetingInPast && (
        // If a meeting is in the past, just show the "Modify meeting" button, which
        // shows reschedule meeting modal
        <button
          data-testid="modify-past-meeting-button"
          className="ae-link brand-typography-body6"
          onClick={handleModifyMeeting}
        >
          Modify meeting
        </button>
      )}

      {isMeetingScheduled && !isMeetingInPast && (
        <div data-testid="status-action-menu">
          {/*Otherwise, show action menu with both "Modify meeting" and "Cancel meeting" buttons*/}
          <ActionMenu
            trigger="Edit meeting"
            triggerClassName="ae-link brand-typography-body6 cursor-pointer"
            actions={meetingScheduledActionItems}
            itemClassName="w-max"
          />

          <AccountCancelMeetingConfirmationModal
            campaignId={campaign?.id}
            accountId={account?.id}
            onSuccess={onAccountUpdate}
          />
        </div>
      )}

      {isCold && <ActionMoveToNurture />}

      <AccountTopTenAction
        campaignId={campaign?.id}
        accountId={account?.id}
        accountStatus={accountStatus}
        onAccountUpdate={onAccountUpdate}
      />
    </div>
  );
};
